/**
 * ChatHighlightsButton
 *
 * Header popover for saved chat message highlights.
 *
 * Primary responsibilities:
 * - Show an icon-only highlights trigger when highlights exist
 * - Render full saved excerpts without truncation
 * - Dispatch scroll requests when a highlight is selected
 *
 * Dependencies:
 * - Chat highlight event helpers
 * - Popover and button UI primitives
 */

import React, { useId, useMemo, useState } from 'react'
import { IconHighlight, IconX } from '@tabler/icons-react'

import { dispatchChatHighlightOpen, type ChatHighlight } from '@/lib/chat/chat-highlights'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'

interface ChatHighlightsButtonProps {
  highlights: ChatHighlight[]
  onDeleteHighlight?: (highlightId: string) => void
}

// --------------

function sortHighlights(highlights: ChatHighlight[]) {
  return [...highlights].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  )
}

// --------------

export const ChatHighlightsButton: React.FC<ChatHighlightsButtonProps> = ({
  highlights,
  onDeleteHighlight
}) => {
  const [open, setOpen] = useState(false)
  const titleId = useId()
  const sortedHighlights = useMemo(() => sortHighlights(highlights), [highlights])

  if (sortedHighlights.length === 0) return null

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          size="icon"
          variant="ghost"
          aria-label={`Open ${sortedHighlights.length} saved highlights`}
          aria-expanded={open}
          aria-haspopup="dialog"
          className="relative size-9 rounded-full border border-transparent text-amber-600 transition-colors hover:border-amber-300/60 hover:bg-amber-500/10 data-[state=open]:border-amber-300/70 data-[state=open]:bg-amber-500/15 dark:text-amber-300"
          title="Highlights"
        >
          <IconHighlight aria-hidden="true" className="size-[19px]" stroke={1.9} />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        collisionPadding={12}
        aria-labelledby={titleId}
        className="max-h-[70vh] w-[min(28rem,calc(100vw-1rem))] overflow-hidden rounded-lg p-0 shadow-xl shadow-black/10 dark:shadow-black/30"
      >
        <div className="flex items-center justify-between border-b bg-amber-500/5 px-3 py-2.5">
          <p id={titleId} className="text-sm font-semibold">
            Highlights
          </p>
          <p className="text-muted-foreground text-xs font-medium">
            {sortedHighlights.length}
          </p>
        </div>
        <ul className="hide-scrollbar m-0 max-h-[calc(70vh-3rem)] touch-pan-y list-none divide-y overflow-y-auto overscroll-contain p-0">
          {sortedHighlights.map((highlight) => (
            <li key={highlight.id} className="group flex items-start gap-1">
              <button
                type="button"
                className="focus-visible:ring-primary/40 min-w-0 flex-1 px-3 py-2.5 text-left transition-colors hover:bg-amber-500/10 focus-visible:bg-amber-500/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset"
                onClick={() => {
                  dispatchChatHighlightOpen({
                    highlightId: highlight.id,
                    messageId: highlight.messageId
                  })
                  setOpen(false)
                }}
              >
                <span className="sr-only">Open highlight: </span>
                <span className="text-foreground block whitespace-pre-wrap break-words text-sm leading-6">
                  {highlight.text}
                </span>
              </button>
              {onDeleteHighlight ? (
                <button
                  type="button"
                  aria-label="Remove saved highlight"
                  className="text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:ring-primary/40 mr-1 mt-2 inline-flex size-7 shrink-0 items-center justify-center rounded-full opacity-70 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 group-hover:opacity-100"
                  onClick={(event) => {
                    event.stopPropagation()
                    onDeleteHighlight(highlight.id)
                  }}
                >
                  <IconX aria-hidden="true" className="size-4" stroke={1.8} />
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  )
}
